import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faTrash, 
  faSpinner, 
  faExclamationTriangle,
  faMusic,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { 
  faSpotify, 
  faApple, 
  faSoundcloud, 
  faYoutube, 
  faVimeo 
} from '@fortawesome/free-brands-svg-icons';
import styles from './EPKBuilder.module.css';

const MusicVideosStep = ({ data, updateData, errors, mode }) => {
  const [musicData, setMusicData] = useState(() => ({
    spotify: data.music?.spotify || [],
    appleMusic: data.music?.appleMusic || [],
    soundcloud: data.music?.soundcloud || []
  }));
  
  const [videos, setVideos] = useState(() => data.videos || []);

  const [activeTab, setActiveTab] = useState('spotify');
  
  // Form state for adding new items
  const [newSpotifyUrl, setNewSpotifyUrl] = useState('');
  const [newAppleMusicUrl, setNewAppleMusicUrl] = useState('');
  const [newSoundcloudUrl, setNewSoundcloudUrl] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  
  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Use refs to prevent update loops
  const initialRender = useRef(true);
  const prevMusicDataRef = useRef(musicData);
  const prevVideosRef = useRef(videos);
  
  // Update parent component when data changes
  useEffect(() => {
    // Skip the update on the initial render
    if (initialRender.current) {
      initialRender.current = false;
      prevMusicDataRef.current = musicData;
      prevVideosRef.current = videos;
      return;
    }
    
    // Only update if data has actually changed
    if (JSON.stringify(prevMusicDataRef.current) !== JSON.stringify(musicData) || 
        JSON.stringify(prevVideosRef.current) !== JSON.stringify(videos)) {
      
      console.log('MusicVideosStep: Syncing with parent component', { musicData, videos });
      updateData({
        music: musicData,
        videos
      });
      
      // Update refs
      prevMusicDataRef.current = musicData;
      prevVideosRef.current = videos;
    }
  }, [musicData, videos, updateData]);

  // Function to extract track ID from Spotify URL
  const extractSpotifyId = (url) => {
    const regex = /track\/([a-zA-Z0-9]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };
  
  // Function to extract Apple Music embed URL
  const extractAppleMusicEmbed = (url) => {
    // Convert standard URL to embed format if needed
    if (url.includes('music.apple.com')) {
      // Apple Music URLs are complex, this is a simplified approach
      const albumMatch = url.match(/album\/[^/]+\/([0-9]+)/);
      const songMatch = url.match(/\/([0-9]+)\?i=([0-9]+)/);
      
      if (albumMatch && songMatch) {
        return {
          embedUrl: `https://embed.music.apple.com/us/album/${albumMatch[1]}?i=${songMatch[2]}`,
          originalUrl: url
        };
      }
    }
    
    // If already an embed URL or not recognized, return as is
    return { embedUrl: url, originalUrl: url };
  };
  
  // Function to extract SoundCloud URL
  const processSoundcloudUrl = (url) => {
    // SoundCloud URLs are simple to work with
    return { url };
  };
  
  // Function to extract YouTube/Vimeo video ID and create embed URL
  const processVideoUrl = (url) => {
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId;
      
      if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(new URL(url).search);
        videoId = urlParams.get('v');
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      }
      
      if (videoId) {
        return {
          platform: 'youtube',
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
          originalUrl: url
        };
      }
    }
    
    // Vimeo
    if (url.includes('vimeo.com')) {
      const vimeoRegex = /vimeo\.com\/([0-9]+)/;
      const match = url.match(vimeoRegex);
      
      if (match && match[1]) {
        return {
          platform: 'vimeo',
          embedUrl: `https://player.vimeo.com/video/${match[1]}`,
          originalUrl: url
        };
      }
    }
    
    return null;
  };
  
  // Handle adding Spotify track
  const handleAddSpotifyTrack = () => {
    if (!newSpotifyUrl) return;
    setIsLoading(true);
    setError(null);
    
    try {
      const trackId = extractSpotifyId(newSpotifyUrl);
      
      if (!trackId) {
        throw new Error('Invalid Spotify URL. Please use a Spotify track link.');
      }
      
      // Check if track already exists
      if (musicData.spotify.some(track => track.id === trackId)) {
        throw new Error('This track is already in your EPK.');
      }
      
      // Add the track
      setMusicData(prev => ({
        ...prev,
        spotify: [...prev.spotify, {
          id: trackId,
          url: newSpotifyUrl,
          addedAt: new Date().toISOString()
        }]
      }));
      
      // Reset input
      setNewSpotifyUrl('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle adding Apple Music track
  const handleAddAppleMusicTrack = () => {
    if (!newAppleMusicUrl) return;
    setIsLoading(true);
    setError(null);
    
    try {
      const { embedUrl, originalUrl } = extractAppleMusicEmbed(newAppleMusicUrl);
      
      if (!embedUrl) {
        throw new Error('Invalid Apple Music URL. Please use an Apple Music track link.');
      }
      
      // Check if track already exists
      if (musicData.appleMusic.some(track => track.embedUrl === embedUrl)) {
        throw new Error('This track is already in your EPK.');
      }
      
      // Add the track
      setMusicData(prev => ({
        ...prev,
        appleMusic: [...prev.appleMusic, {
          embedUrl,
          originalUrl,
          addedAt: new Date().toISOString()
        }]
      }));
      
      // Reset input
      setNewAppleMusicUrl('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle adding SoundCloud track
  const handleAddSoundcloudTrack = () => {
    if (!newSoundcloudUrl) return;
    setIsLoading(true);
    setError(null);
    
    try {
      const { url } = processSoundcloudUrl(newSoundcloudUrl);
      
      if (!url) {
        throw new Error('Invalid SoundCloud URL. Please use a SoundCloud track link.');
      }
      
      // Check if track already exists
      if (musicData.soundcloud.some(track => track.url === url)) {
        throw new Error('This track is already in your EPK.');
      }
      
      // Add the track
      setMusicData(prev => ({
        ...prev,
        soundcloud: [...prev.soundcloud, {
          url,
          addedAt: new Date().toISOString()
        }]
      }));
      
      // Reset input
      setNewSoundcloudUrl('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle adding video
  const handleAddVideo = () => {
    if (!newVideoUrl) return;
    setIsLoading(true);
    setError(null);
    
    try {
      const videoData = processVideoUrl(newVideoUrl);
      
      if (!videoData) {
        throw new Error('Invalid video URL. Please use a YouTube or Vimeo link.');
      }
      
      // Check if video already exists
      if (videos.some(video => video.embedUrl === videoData.embedUrl)) {
        throw new Error('This video is already in your EPK.');
      }
      
      // Add the video
      setVideos(prev => [...prev, {
        ...videoData,
        title: newVideoTitle || '',
        addedAt: new Date().toISOString()
      }]);
      
      // Reset inputs
      setNewVideoUrl('');
      setNewVideoTitle('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle removing Spotify track
  const handleRemoveSpotifyTrack = (trackId) => {
    setMusicData(prev => ({
      ...prev,
      spotify: prev.spotify.filter(track => track.id !== trackId)
    }));
  };
  
  // Handle removing Apple Music track
  const handleRemoveAppleMusicTrack = (embedUrl) => {
    setMusicData(prev => ({
      ...prev,
      appleMusic: prev.appleMusic.filter(track => track.embedUrl !== embedUrl)
    }));
  };
  
  // Handle removing SoundCloud track
  const handleRemoveSoundcloudTrack = (url) => {
    setMusicData(prev => ({
      ...prev,
      soundcloud: prev.soundcloud.filter(track => track.url !== url)
    }));
  };
  
  // Handle removing video
  const handleRemoveVideo = (embedUrl) => {
    setVideos(prev => prev.filter(video => video.embedUrl !== embedUrl));
  };
  
  return (
    <div className={`${styles.formSection} ${mode === 'view' ? styles.viewMode : ''}`}>
      <h2 className={styles.sectionTitle}>Music & Videos</h2>
      <p className={styles.sectionDescription}>
        Add your top songs from streaming platforms and featured videos to showcase your work.
      </p>
      
      {/* Music Section */}
      <div className={styles.musicSection}>
        <h3>Featured Music</h3>
        
        <div className={styles.methodTabs}>
          <button 
            className={`${styles.methodTab} ${activeTab === 'spotify' ? styles.active : ''}`}
            onClick={() => setActiveTab('spotify')}
          >
            <FontAwesomeIcon icon={faSpotify} /> Spotify
          </button>
          
          <button 
            className={`${styles.methodTab} ${activeTab === 'appleMusic' ? styles.active : ''}`}
            onClick={() => setActiveTab('appleMusic')}
          >
            <FontAwesomeIcon icon={faApple} /> Apple Music
          </button>
          
          <button 
            className={`${styles.methodTab} ${activeTab === 'soundcloud' ? styles.active : ''}`}
            onClick={() => setActiveTab('soundcloud')}
          >
            <FontAwesomeIcon icon={faSoundcloud} /> SoundCloud
          </button>
        </div>
        
        {/* Spotify Tab */}
        {activeTab === 'spotify' && (
          <div className={styles.tabContent}>
            <div className={styles.embedInput}>
              <input
                type="text"
                value={newSpotifyUrl}
                onChange={(e) => setNewSpotifyUrl(e.target.value)}
                placeholder="Paste Spotify track URL (e.g., https://open.spotify.com/track/...)"
                disabled={isLoading || mode === 'view'}
              />
              
              <button
                className={styles.embedBtn}
                onClick={handleAddSpotifyTrack}
                disabled={isLoading || !newSpotifyUrl || mode === 'view'}
              >
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                Add Track
              </button>
            </div>
            
            {error && (
              <div className={styles.embedError}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}
            
            {musicData.spotify.length > 0 ? (
              <div className={styles.embedsList}>
                {musicData.spotify.map((track, index) => (
                  <div key={`spotify-${track.id}`} className={styles.embedPreview}>
                    <div className={styles.embedPreviewHeader}>
                      <div className={styles.embedPreviewTitle}>
                        <FontAwesomeIcon icon={faSpotify} /> Spotify Track
                      </div>
                      
                      {mode !== 'view' && (
                        <button
                          className={styles.embedPreviewDelete}
                          onClick={() => handleRemoveSpotifyTrack(track.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    
                    <div className={styles.embedPreviewContent}>
                      <iframe
                        src={`https://open.spotify.com/embed/track/${track.id}`}
                        width="100%"
                        height="80"
                        frameBorder="0"
                        allowtransparency="true"
                        allow="encrypted-media"
                        title={`Spotify Track ${index + 1}`}
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faMusic} className={styles.emptyIcon} />
                <p>No Spotify tracks added yet</p>
              </div>
            )}
          </div>
        )}
        
        {/* Apple Music Tab */}
        {activeTab === 'appleMusic' && (
          <div className={styles.tabContent}>
            <div className={styles.embedInput}>
              <input
                type="text"
                value={newAppleMusicUrl}
                onChange={(e) => setNewAppleMusicUrl(e.target.value)}
                placeholder="Paste Apple Music track URL"
                disabled={isLoading || mode === 'view'}
              />
              
              <button
                className={styles.embedBtn}
                onClick={handleAddAppleMusicTrack}
                disabled={isLoading || !newAppleMusicUrl || mode === 'view'}
              >
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                Add Track
              </button>
            </div>
            
            {error && (
              <div className={styles.embedError}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}
            
            {musicData.appleMusic.length > 0 ? (
              <div className={styles.embedsList}>
                {musicData.appleMusic.map((track, index) => (
                  <div key={`apple-${index}`} className={styles.embedPreview}>
                    <div className={styles.embedPreviewHeader}>
                      <div className={styles.embedPreviewTitle}>
                        <FontAwesomeIcon icon={faApple} /> Apple Music Track
                      </div>
                      
                      {mode !== 'view' && (
                        <button
                          className={styles.embedPreviewDelete}
                          onClick={() => handleRemoveAppleMusicTrack(track.embedUrl)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    
                    <div className={styles.embedPreviewContent}>
                      <iframe
                        allow="autoplay *; encrypted-media *; fullscreen *"
                        frameBorder="0"
                        height="150"
                        width="100%"
                        sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                        src={track.embedUrl}
                        title={`Apple Music Track ${index + 1}`}
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faMusic} className={styles.emptyIcon} />
                <p>No Apple Music tracks added yet</p>
              </div>
            )}
          </div>
        )}
        
        {/* SoundCloud Tab */}
        {activeTab === 'soundcloud' && (
          <div className={styles.tabContent}>
            <div className={styles.embedInput}>
              <input
                type="text"
                value={newSoundcloudUrl}
                onChange={(e) => setNewSoundcloudUrl(e.target.value)}
                placeholder="Paste SoundCloud track URL"
                disabled={isLoading || mode === 'view'}
              />
              
              <button
                className={styles.embedBtn}
                onClick={handleAddSoundcloudTrack}
                disabled={isLoading || !newSoundcloudUrl || mode === 'view'}
              >
                {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                Add Track
              </button>
            </div>
            
            {error && (
              <div className={styles.embedError}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}
            
            {musicData.soundcloud.length > 0 ? (
              <div className={styles.embedsList}>
                {musicData.soundcloud.map((track, index) => (
                  <div key={`soundcloud-${index}`} className={styles.embedPreview}>
                    <div className={styles.embedPreviewHeader}>
                      <div className={styles.embedPreviewTitle}>
                        <FontAwesomeIcon icon={faSoundcloud} /> SoundCloud Track
                      </div>
                      
                      {mode !== 'view' && (
                        <button
                          className={styles.embedPreviewDelete}
                          onClick={() => handleRemoveSoundcloudTrack(track.url)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                    
                    <div className={styles.embedPreviewContent}>
                      <iframe
                        width="100%"
                        height="166"
                        scrolling="no"
                        frameBorder="no"
                        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(track.url)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true`}
                        title={`SoundCloud Track ${index + 1}`}
                      ></iframe>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faMusic} className={styles.emptyIcon} />
                <p>No SoundCloud tracks added yet</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Videos Section */}
      <div className={styles.videoSection}>
        <h3>Featured Videos</h3>
        
        <div className={styles.embedInput}>
          <div className={styles.formGroup} style={{ flex: 1 }}>
            <input
              type="text"
              value={newVideoUrl}
              onChange={(e) => setNewVideoUrl(e.target.value)}
              placeholder="Paste YouTube or Vimeo video URL"
              disabled={isLoading || mode === 'view'}
              style={{ marginBottom: '8px' }}
            />
            
            <input
              type="text"
              value={newVideoTitle}
              onChange={(e) => setNewVideoTitle(e.target.value)}
              placeholder="Video title (optional)"
              disabled={isLoading || mode === 'view'}
            />
          </div>
          
          <button
            className={styles.embedBtn}
            onClick={handleAddVideo}
            disabled={isLoading || !newVideoUrl || mode === 'view'}
            style={{ alignSelf: 'flex-start' }}
          >
            {isLoading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
            Add Video
          </button>
        </div>
        
        {error && (
          <div className={styles.embedError}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        
        {videos.length > 0 ? (
          <div className={styles.videoGrid}>
            {videos.map((video, index) => (
              <div key={`video-${index}`} className={styles.videoEmbed}>
                <div className={styles.embedPreviewHeader}>
                  <div className={styles.embedPreviewTitle}>
                    <FontAwesomeIcon icon={video.platform === 'youtube' ? faYoutube : faVimeo} />
                    {video.title ? video.title : `${video.platform === 'youtube' ? 'YouTube' : 'Vimeo'} Video ${index + 1}`}
                  </div>
                  
                  {mode !== 'view' && (
                    <button
                      className={styles.embedPreviewDelete}
                      onClick={() => handleRemoveVideo(video.embedUrl)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
                
                <div className={styles.videoEmbedContainer}>
                  <iframe
                    width="100%"
                    height="315"
                    src={video.embedUrl}
                    title={video.title || `Video ${index + 1}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faVideo} className={styles.emptyIcon} />
            <p>No videos added yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicVideosStep;